import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  CreditCard, Download, ExternalLink, AlertTriangle, Loader2,
  FileText, Users, Mic, Calendar, Coins, Receipt, Plus, Send,
} from "lucide-react";
import { PageContainer, PageHeader, Card } from "../components/kit";
import { Link } from "react-router-dom";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";

const fmtMoney = (amount, currency) =>
  amount == null ? "—" : `$${Number(amount).toFixed(2)}${currency && currency !== "USD" ? ` ${currency}` : ""}`;

const STATUS_META = {
  active:   { label: "Active",    cls: "bg-[#10B981]/10 text-[#10B981]" },
  past_due: { label: "Past due",  cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  canceled: { label: "Canceled",  cls: "bg-muted text-muted-foreground" },
  pending:  { label: "Pending",   cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  approved: { label: "Awaiting payment", cls: "bg-[#0025D8]/10 text-[#0025D8]" },
};

const TXN_STATUS_CLS = {
  paid:          "bg-[#10B981]/10 text-[#10B981]",
  open:          "bg-[#F59E0B]/10 text-[#F59E0B]",
  void:          "bg-muted text-muted-foreground",
  uncollectible: "bg-[#E11D48]/10 text-[#E11D48]",
  draft:         "bg-muted text-muted-foreground",
};

// ── Small usage stat tile ──
function UsageTile({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-border bg-white px-4 py-3.5">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`font-display font-bold text-xl ${accent ? "text-[#0025D8]" : "text-[#0F172A]"}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function CompanyBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/company/billing");
      setData(data);
    } catch {
      toast.error("Could not load billing details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/company/billing-portal");
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer width="narrow">
        <div className="py-20 flex items-center justify-center text-muted-foreground font-mono text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading billing…
        </div>
      </PageContainer>
    );
  }

  const statusMeta = STATUS_META[data?.company_status] || null;
  const pm = data?.payment_method;
  const priceCents = data?.plan_price_cents;
  const interviewsRemaining = data?.interview_limit != null
    ? Math.max(0, data.interview_limit - (data.interviews_used || 0))
    : null;
  const isTrial = !!data?.is_trial;
  const trialDaysLeft = data?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(data.trial_ends_at) - Date.now()) / 86400000))
    : null;

  return (
    <PageContainer width="narrow">
      <PageHeader eyebrow="Account" title="Billing & account." />

      {/* ── Plan summary card ── */}
      <Card padding="p-0" className="overflow-hidden mb-6">
        <div className="relative bg-[#0F172A] px-6 py-6 overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(0,37,216,0.30) 0%, transparent 70%)" }} />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#818CF8] font-bold">
                  {data?.plan_label || "Plan"}
                </span>
                {statusMeta && (
                  <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ml-1 ${statusMeta.cls}`}>
                    {statusMeta.label}
                  </span>
                )}
              </div>
              <div className="font-display font-black text-3xl text-white">
                {priceCents != null
                  ? <>{fmtMoney(priceCents / 100)}<span className="text-base font-bold text-white/40"> /{data?.interval || "mo"}</span></>
                  : (data?.plan_label || "—")}
              </div>
              <p className="text-xs text-white/50 mt-1.5 font-mono">
                {isTrial
                  ? `Free trial — ${trialDaysLeft === 0 ? "ends today" : `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left`}`
                  : data?.cancel_at_period_end
                    ? `Cancels on ${fmtDate(data.current_period_end)}`
                    : data?.period_end
                      ? `Next cycle resets ${fmtDate(data.period_end)}`
                      : "Monthly billing"}
              </p>
            </div>
          </div>

          {data?.cancel_at_period_end && (
            <div className="relative mt-4 flex items-start gap-2 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 px-3 py-2.5">
              <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#FCD34D] leading-snug">
                Your subscription is set to cancel. Access continues until {fmtDate(data.current_period_end)}.
              </p>
            </div>
          )}
        </div>

        {/* Payment method + manage */}
        <div className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5 text-sm">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            {pm ? (
              <span className="text-[#0F172A]">
                <span className="capitalize font-medium">{pm.brand}</span> ···· {pm.last4}
                <span className="text-muted-foreground font-mono text-xs ml-2">
                  exp {String(pm.exp_month).padStart(2, "0")}/{String(pm.exp_year).slice(-2)}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">No card on file</span>
            )}
          </div>
          {data?.has_billing_account && (
            <button onClick={openPortal} disabled={portalLoading}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white px-4 py-2.5 text-xs font-bold transition-colors disabled:opacity-60">
              {portalLoading
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening…</>
                : <>Manage billing <ExternalLink className="w-3.5 h-3.5" /></>}
            </button>
          )}
        </div>
        <p className="px-6 pb-4 -mt-1 font-mono text-[10px] text-muted-foreground">
          Update your card, download invoices, or manage the subscription — securely via Stripe.
        </p>
      </Card>

      {/* ── Usage tiles ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <UsageTile
          icon={Users} label="Seats"
          value={data?.effective_seat_limit != null ? `${data.seats_used} / ${data.effective_seat_limit}` : data?.seats_used}
          sub={data?.addon_seats ? `+${data.addon_seats} paid` : "included"}
        />
        <UsageTile
          icon={Mic} label="Interviews left"
          value={interviewsRemaining != null ? interviewsRemaining : "Unlimited"}
          sub={data?.interview_limit != null ? `of ${data.interview_limit} this cycle` : null}
          accent
        />
        <UsageTile
          icon={Coins} label="Carry-over credits"
          value={data?.interview_credits || 0}
          sub={data?.interview_credits ? "never expire" : "buy a top-up"}
        />
        <UsageTile
          icon={Send} label="Sent all-time"
          value={data?.total_invites_sent || 0}
          sub="across all cycles"
        />
        <UsageTile
          icon={Calendar} label="Cycle resets"
          value={data?.period_end ? fmtDate(data.period_end) : "—"}
          sub="allowance renews"
        />
      </div>

      {/* Add-ons shortcut */}
      <div className="flex items-center justify-between gap-3 mb-6 rounded-xl border border-dashed border-border bg-secondary/30 px-4 py-3">
        <p className="text-xs text-muted-foreground">Need more capacity? Buy extra seats or interview credit packs.</p>
        <Link to="/company/roles" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0025D8] hover:underline flex-shrink-0">
          <Plus className="w-3.5 h-3.5" /> Manage add-ons
        </Link>
      </div>

      {/* ── Transaction history ── */}
      <Card padding="p-0" className="overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-display font-bold text-base">Transaction history</h2>
        </div>

        {!data?.transactions?.length ? (
          <div className="px-6 py-10 text-center">
            <p className="text-sm text-muted-foreground">
              No transactions yet — your plan invoices and credit-pack purchases will appear here.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.transactions.map((t, i) => {
              const stCls = TXN_STATUS_CLS[t.status] || "bg-muted text-muted-foreground";
              const isTopup = t.type === "topup";
              return (
                <div key={i} className="px-6 py-3.5 flex items-center justify-between gap-4 hover:bg-secondary/30 transition-colors">
                  <div className="min-w-0 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isTopup ? "bg-[#0025D8]/10 text-[#0025D8]" : "bg-secondary text-muted-foreground"}`}>
                      {isTopup ? <Coins className="w-4 h-4" /> : <Receipt className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0F172A] truncate">{t.description}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">
                        {fmtDate(t.date)}{isTopup ? " · one-time" : " · subscription"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-mono text-sm text-[#0F172A]">{fmtMoney(t.amount, t.currency)}</span>
                    {t.status && (
                      <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${stCls}`}>
                        {t.status}
                      </span>
                    )}
                    {t.pdf_url ? (
                      <a href={t.pdf_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#0025D8] hover:underline text-xs font-medium" title="Download PDF">
                        <Download className="w-3.5 h-3.5" /> PDF
                      </a>
                    ) : <span className="w-12" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <p className="text-center text-[11px] text-muted-foreground mt-6">
        Payments are processed securely by Stripe. Questions about billing?{" "}
        <a href="mailto:info@eminencedigital.io" className="text-[#0025D8] hover:underline">Contact us</a>.
      </p>
    </PageContainer>
  );
}
